import { Typography, Select, Segmented, Button, Switch } from 'antd';
import { useNavigate } from 'react-router-dom';

import type { TimeFormat } from '@app/api/Branches/types';
import { SETTINGS_PATH } from '@app/constants/navigation';

import { useCalendarDisplaySettingsForm } from '@pages/CalendarSettings/hooks/useCalendarDisplaySettingsForm';
import { useCalendarSettings } from '@pages/CalendarSettings/hooks/useCalendarSettings';

import { AppointmentPreviewSettings } from '@pages/Calendar/ui/AppointmentPreviewSettings';

import '@pages/CalendarSettings/styles/CalendarSettings.desktop.scss';

const PF = 'calendar-settings';
const { Text } = Typography;

export const CalendarSettings = () => {
  const navigate = useNavigate();

  const {
    settings,
    onChange,
    isLoadingPage,
    isSavingSettings,
    options,
    canEditRecordTagsWindow,
    saveSettingsHandler,
  } = useCalendarSettings();
  const { timeFormat, isRecordTagsWindow } = settings;

  // Те же настройки отображения, что в поповере журнала (task/11150): у них
  // своё локальное хранилище на пользователя и филиал, поэтому они
  // применяются сразу и кнопкой «Сохранить» не управляются.
  const {
    intervalHeightSize,
    intervalHeightSizeOptions,
    gridStepMinutes,
    gridStepMinuteOptions,
    preferredMaxColumns,
    preferredMaxColumnOptions,
    intervalHeightSizeChangeHandler,
    gridStepMinutesChangeHandler,
    preferredMaxColumnsChangeHandler,
    previewFields,
    previewFieldsChangeHandler,
  } = useCalendarDisplaySettingsForm();

  return (
    <div className={`${PF} ${PF}_wrapper`}>
      <div className={`${PF}_container`}>
        <div className={`${PF}_calendar`}>
          <div className={`${PF}_calendar_header`}>
            <Text
              className={`${PF}_calendar_title`}
              strong
              data-testid="time-format-title"
            >
              Формат времени
            </Text>
            <Text data-testid="time-format-description">
              Выберите формат времени отображаемый в Журнале записи
            </Text>
          </div>
          <Select
            className={`${PF}_calendar_select`}
            value={timeFormat}
            onChange={(value: string) =>
              onChange('timeFormat', value as TimeFormat)
            }
            options={options}
            disabled={isLoadingPage}
            loading={isLoadingPage}
            placeholder="Выберите формат времени"
            popupRender={(menu) => (
              <div data-testid="time-format-dropdown">{menu}</div>
            )}
            optionRender={(option) => (
              <span data-testid="format-time-item">{option.label}</span>
            )}
          />
        </div>
      </div>
      {canEditRecordTagsWindow && (
        <div className={`${PF}_container`}>
          <div className={`${PF}_calendar`}>
            <div className={`${PF}_calendar_header`}>
              <Text
                className={`${PF}_calendar_title`}
                strong
                data-testid="record-tags-window-title"
              >
                Показывать окно тегов при создании записи
              </Text>
              <Text>
                После сохранения новой записи менеджер увидит окно с типом
                клиента, типом записи, каналом обращения и стоимостью
              </Text>
            </div>
            <Switch
              className={`${PF}_calendar_switch`}
              checked={isRecordTagsWindow}
              disabled={isLoadingPage}
              onChange={(checked) => onChange('isRecordTagsWindow', checked)}
              data-testid="record-tags-window-switch"
            />
          </div>
        </div>
      )}

      <div className={`${PF}_container`}>
        <div className={`${PF}_calendar`}>
          <div className={`${PF}_calendar_header`}>
            <Text className={`${PF}_calendar_title`} strong>
              Высота секции
            </Text>
            <Text>Управляет вертикальной плотностью календарной сетки</Text>
          </div>
          <Segmented
            className={`${PF}_calendar_select`}
            value={intervalHeightSize}
            options={intervalHeightSizeOptions}
            onChange={intervalHeightSizeChangeHandler}
          />
        </div>

        <div className={`${PF}_calendar`}>
          <div className={`${PF}_calendar_header`}>
            <Text className={`${PF}_calendar_title`} strong>
              Длительность секции
            </Text>
            <Text>
              Определяет шаг сетки для клика, наведения и перетаскивания
            </Text>
          </div>
          <Select
            className={`${PF}_calendar_select`}
            value={gridStepMinutes}
            options={gridStepMinuteOptions}
            onChange={gridStepMinutesChangeHandler}
          />
        </div>
      </div>

      <div className={`${PF}_container`}>
        <div className={`${PF}_calendar`}>
          <div className={`${PF}_calendar_header`}>
            <Text className={`${PF}_calendar_title`} strong>
              Максимум колонок
            </Text>
            <Text>
              Предпочитаемый максимум; фактическое число зависит от ширины окна
            </Text>
          </div>
          <Select
            className={`${PF}_calendar_select`}
            value={preferredMaxColumns}
            options={preferredMaxColumnOptions}
            onChange={preferredMaxColumnsChangeHandler}
          />
        </div>
      </div>

      <div className={`${PF}_container`}>
        <div className={`${PF}_calendar`}>
          <div className={`${PF}_calendar_header`}>
            <Text className={`${PF}_calendar_title`} strong>
              Превью записи
            </Text>
            <Text>
              Какие строки показывать в записи в сетке журнала и в каком порядке
            </Text>
          </div>
          <AppointmentPreviewSettings
            fields={previewFields}
            onFieldsChange={previewFieldsChangeHandler}
          />
        </div>
      </div>

      <div className={`${PF}_container`}>
        <div className={`${PF}_footer`}>
          <Button
            disabled={isLoadingPage}
            loading={isSavingSettings}
            type="primary"
            onClick={saveSettingsHandler}
            data-testid="save"
          >
            Сохранить
          </Button>
          <Button
            loading={isSavingSettings}
            disabled={isLoadingPage}
            type="default"
            onClick={() => navigate(SETTINGS_PATH)}
            data-testid="cancel"
          >
            Отмена
          </Button>
        </div>
      </div>
    </div>
  );
};
